import { GitBranch, Lock, Network, Plus, Save, Tags } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { OntologyType, RelationType } from "../types";

type Props = {
  types: OntologyType[];
  relationTypes: RelationType[];
  onCreateType: (input: { slug: string; name: string; description?: string; parentTypeId?: string | null }) => Promise<void>;
  onUpdateType: (id: string, input: { name?: string; description?: string; parentTypeId?: string | null }) => Promise<void>;
  onCreateRelationType: (input: {
    slug: string;
    name: string;
    description?: string;
    sourceTypeId?: string | null;
    targetTypeId?: string | null;
    inverseSlug?: string | null;
    directional?: boolean;
  }) => Promise<void>;
  onUpdateRelationType: (
    id: string,
    input: {
      name?: string;
      description?: string;
      sourceTypeId?: string | null;
      targetTypeId?: string | null;
      inverseSlug?: string | null;
      directional?: boolean;
    }
  ) => Promise<void>;
  error?: string;
};

const emptyType = { slug: "", name: "", description: "", parentTypeId: "" };
const emptyRelation = {
  slug: "",
  name: "",
  description: "",
  sourceTypeId: "",
  targetTypeId: "",
  inverseSlug: "",
  directional: true
};

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function OntologyManagerPanel({
  types,
  relationTypes,
  onCreateType,
  onUpdateType,
  onCreateRelationType,
  onUpdateRelationType,
  error
}: Props) {
  const [typeDraft, setTypeDraft] = useState(emptyType);
  const [relationDraft, setRelationDraft] = useState(emptyRelation);
  const [editingTypeId, setEditingTypeId] = useState("");
  const [editingRelationId, setEditingRelationId] = useState("");
  const [editType, setEditType] = useState({ name: "", description: "", parentTypeId: "" });
  const [editRelation, setEditRelation] = useState({
    name: "",
    description: "",
    sourceTypeId: "",
    targetTypeId: "",
    inverseSlug: "",
    directional: true
  });
  const [saving, setSaving] = useState(false);

  const editableTypes = useMemo(() => types.filter((type) => !type.system), [types]);
  const editableRelations = useMemo(() => relationTypes.filter((relation) => !relation.system), [relationTypes]);

  useEffect(() => {
    const type = types.find((item) => item.id === editingTypeId);
    if (type) {
      setEditType({ name: type.name, description: type.description, parentTypeId: type.parentTypeId ?? "" });
    }
  }, [editingTypeId, types]);

  useEffect(() => {
    const relation = relationTypes.find((item) => item.id === editingRelationId);
    if (relation) {
      setEditRelation({
        name: relation.name,
        description: relation.description,
        sourceTypeId: relation.sourceTypeId ?? "",
        targetTypeId: relation.targetTypeId ?? "",
        inverseSlug: relation.inverseSlug ?? "",
        directional: relation.directional
      });
    }
  }, [editingRelationId, relationTypes]);

  const handleCreateType = async () => {
    const slug = slugify(typeDraft.slug || typeDraft.name);
    if (!slug || !typeDraft.name.trim()) {
      return;
    }

    setSaving(true);
    try {
      await onCreateType({
        slug,
        name: typeDraft.name.trim(),
        description: typeDraft.description.trim(),
        parentTypeId: typeDraft.parentTypeId || null
      });
      setTypeDraft(emptyType);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateType = async () => {
    if (!editingTypeId || !editType.name.trim()) {
      return;
    }

    setSaving(true);
    try {
      await onUpdateType(editingTypeId, {
        name: editType.name.trim(),
        description: editType.description.trim(),
        parentTypeId: editType.parentTypeId || null
      });
      setEditingTypeId("");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRelation = async () => {
    const slug = slugify(relationDraft.slug || relationDraft.name);
    if (!slug || !relationDraft.name.trim()) {
      return;
    }

    setSaving(true);
    try {
      await onCreateRelationType({
        slug,
        name: relationDraft.name.trim(),
        description: relationDraft.description.trim(),
        sourceTypeId: relationDraft.sourceTypeId || null,
        targetTypeId: relationDraft.targetTypeId || null,
        inverseSlug: relationDraft.inverseSlug.trim() || null,
        directional: relationDraft.directional
      });
      setRelationDraft(emptyRelation);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRelation = async () => {
    if (!editingRelationId || !editRelation.name.trim()) {
      return;
    }

    setSaving(true);
    try {
      await onUpdateRelationType(editingRelationId, {
        name: editRelation.name.trim(),
        description: editRelation.description.trim(),
        sourceTypeId: editRelation.sourceTypeId || null,
        targetTypeId: editRelation.targetTypeId || null,
        inverseSlug: editRelation.inverseSlug.trim() || null,
        directional: editRelation.directional
      });
      setEditingRelationId("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-line bg-white shadow-panel">
      <div className="border-b border-line px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-2 inline-flex h-8 items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 text-xs font-bold text-brand">
              <Network size={15} aria-hidden="true" />
              Ontology
            </div>
            <h2 className="text-base font-bold text-ink">Thực thể và mối quan hệ</h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              Node là entity/object, edge là relation, label là type. Type hệ thống chỉ đọc; type của bạn có thể sửa.
            </p>
          </div>
          <span className="rounded-md border border-line bg-slate-50 px-2 py-1 text-xs font-bold text-muted">
            {types.length} types · {relationTypes.length} relations
          </span>
        </div>
      </div>

      <div className="grid gap-4 p-4 xl:grid-cols-2">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted">
            <Tags size={14} aria-hidden="true" />
            Entity Types
          </div>
          <div className="rounded-lg border border-line bg-slate-50 p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                value={typeDraft.name}
                onChange={(event) => setTypeDraft((current) => ({ ...current, name: event.target.value }))}
                placeholder="Person, Claim, Framework"
                aria-label="Entity type name"
              />
              <input
                className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                value={typeDraft.slug}
                onChange={(event) => setTypeDraft((current) => ({ ...current, slug: slugify(event.target.value) }))}
                placeholder="person"
                aria-label="Entity type slug"
              />
              <select
                className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-slate-700 sm:col-span-2"
                value={typeDraft.parentTypeId}
                onChange={(event) => setTypeDraft((current) => ({ ...current, parentTypeId: event.target.value }))}
                aria-label="Parent entity type"
              >
                <option value="">No parent</option>
                {types.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              <input
                className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-ink sm:col-span-2"
                value={typeDraft.description}
                onChange={(event) => setTypeDraft((current) => ({ ...current, description: event.target.value }))}
                placeholder="Dùng để lưu người, công ty, luận điểm..."
                aria-label="Entity type description"
              />
            </div>
            <button
              className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-brand px-3 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-slate-300"
              type="button"
              disabled={saving || !typeDraft.name.trim()}
              onClick={() => void handleCreateType()}
            >
              <Plus size={16} aria-hidden="true" />
              Tạo entity type
            </button>
          </div>

          <div className="space-y-2">
            {types.map((type) => (
              <article key={type.id} className="rounded-lg border border-line bg-white p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-sm font-bold">{type.name}</h3>
                    <p className="mt-1 text-xs font-semibold text-muted">{type.slug}</p>
                    {type.description ? <p className="mt-2 text-sm leading-6 text-slate-600">{type.description}</p> : null}
                  </div>
                  {type.system ? (
                    <span className="inline-flex h-7 items-center gap-1 rounded-md border border-line bg-slate-50 px-2 text-xs font-bold text-muted">
                      <Lock size={12} aria-hidden="true" />
                      system
                    </span>
                  ) : (
                    <button
                      className="inline-flex h-8 items-center rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
                      type="button"
                      onClick={() => setEditingTypeId(type.id)}
                    >
                      Sửa
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted">
            <GitBranch size={14} aria-hidden="true" />
            Relation Types
          </div>
          <div className="rounded-lg border border-line bg-slate-50 p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                value={relationDraft.name}
                onChange={(event) => setRelationDraft((current) => ({ ...current, name: event.target.value }))}
                placeholder="Example of"
                aria-label="Relation type name"
              />
              <input
                className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                value={relationDraft.slug}
                onChange={(event) => setRelationDraft((current) => ({ ...current, slug: slugify(event.target.value) }))}
                placeholder="example_of"
                aria-label="Relation type slug"
              />
              <select
                className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-slate-700"
                value={relationDraft.sourceTypeId}
                onChange={(event) => setRelationDraft((current) => ({ ...current, sourceTypeId: event.target.value }))}
                aria-label="Relation source type"
              >
                <option value="">Any source type</option>
                {types.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              <select
                className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-slate-700"
                value={relationDraft.targetTypeId}
                onChange={(event) => setRelationDraft((current) => ({ ...current, targetTypeId: event.target.value }))}
                aria-label="Relation target type"
              >
                <option value="">Any target type</option>
                {types.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              <input
                className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                value={relationDraft.inverseSlug}
                onChange={(event) => setRelationDraft((current) => ({ ...current, inverseSlug: slugify(event.target.value) }))}
                placeholder="examples"
                aria-label="Inverse relation slug"
              />
              <label className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={relationDraft.directional}
                  onChange={(event) => setRelationDraft((current) => ({ ...current, directional: event.target.checked }))}
                />
                Directional
              </label>
              <input
                className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-ink sm:col-span-2"
                value={relationDraft.description}
                onChange={(event) => setRelationDraft((current) => ({ ...current, description: event.target.value }))}
                placeholder="Một note là ví dụ của một concept..."
                aria-label="Relation type description"
              />
            </div>
            <button
              className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-brand px-3 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-slate-300"
              type="button"
              disabled={saving || !relationDraft.name.trim()}
              onClick={() => void handleCreateRelation()}
            >
              <Plus size={16} aria-hidden="true" />
              Tạo relation type
            </button>
          </div>

          <div className="space-y-2">
            {relationTypes.map((relation) => (
              <article key={relation.id} className="rounded-lg border border-line bg-white p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-sm font-bold">{relation.name}</h3>
                    <p className="mt-1 text-xs font-semibold text-muted">
                      {relation.slug}
                      {relation.directional ? " · directional" : " · symmetric"}
                    </p>
                    {relation.description ? <p className="mt-2 text-sm leading-6 text-slate-600">{relation.description}</p> : null}
                  </div>
                  {relation.system ? (
                    <span className="inline-flex h-7 items-center gap-1 rounded-md border border-line bg-slate-50 px-2 text-xs font-bold text-muted">
                      <Lock size={12} aria-hidden="true" />
                      system
                    </span>
                  ) : (
                    <button
                      className="inline-flex h-8 items-center rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
                      type="button"
                      onClick={() => setEditingRelationId(relation.id)}
                    >
                      Sửa
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      {editingTypeId ? (
        <div className="border-t border-line bg-blue-50 p-4">
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_180px_auto]">
            <input
              className="h-9 rounded-lg border border-blue-200 bg-white px-3 text-sm text-ink"
              value={editType.name}
              onChange={(event) => setEditType((current) => ({ ...current, name: event.target.value }))}
              aria-label="Edit entity type name"
            />
            <select
              className="h-9 rounded-lg border border-blue-200 bg-white px-3 text-sm text-slate-700"
              value={editType.parentTypeId}
              onChange={(event) => setEditType((current) => ({ ...current, parentTypeId: event.target.value }))}
              aria-label="Edit parent entity type"
            >
              <option value="">No parent</option>
              {types
                .filter((type) => type.id !== editingTypeId)
                .map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
            </select>
            <button
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-brand px-3 text-sm font-bold text-white hover:bg-blue-700"
              type="button"
              disabled={saving || !editableTypes.some((type) => type.id === editingTypeId)}
              onClick={() => void handleUpdateType()}
            >
              <Save size={15} aria-hidden="true" />
              Lưu
            </button>
          </div>
          <input
            className="mt-2 h-9 w-full rounded-lg border border-blue-200 bg-white px-3 text-sm text-ink"
            value={editType.description}
            onChange={(event) => setEditType((current) => ({ ...current, description: event.target.value }))}
            aria-label="Edit entity type description"
          />
        </div>
      ) : null}

      {editingRelationId ? (
        <div className="border-t border-line bg-orange-50 p-4">
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_160px_160px_auto]">
            <input
              className="h-9 rounded-lg border border-orange-200 bg-white px-3 text-sm text-ink"
              value={editRelation.name}
              onChange={(event) => setEditRelation((current) => ({ ...current, name: event.target.value }))}
              aria-label="Edit relation type name"
            />
            <select
              className="h-9 rounded-lg border border-orange-200 bg-white px-3 text-sm text-slate-700"
              value={editRelation.sourceTypeId}
              onChange={(event) => setEditRelation((current) => ({ ...current, sourceTypeId: event.target.value }))}
              aria-label="Edit relation source type"
            >
              <option value="">Any source</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
            <select
              className="h-9 rounded-lg border border-orange-200 bg-white px-3 text-sm text-slate-700"
              value={editRelation.targetTypeId}
              onChange={(event) => setEditRelation((current) => ({ ...current, targetTypeId: event.target.value }))}
              aria-label="Edit relation target type"
            >
              <option value="">Any target</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
            <button
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-brand px-3 text-sm font-bold text-white hover:bg-blue-700"
              type="button"
              disabled={saving || !editableRelations.some((relation) => relation.id === editingRelationId)}
              onClick={() => void handleUpdateRelation()}
            >
              <Save size={15} aria-hidden="true" />
              Lưu
            </button>
          </div>
          <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_160px]">
            <input
              className="h-9 rounded-lg border border-orange-200 bg-white px-3 text-sm text-ink"
              value={editRelation.description}
              onChange={(event) => setEditRelation((current) => ({ ...current, description: event.target.value }))}
              aria-label="Edit relation type description"
            />
            <label className="inline-flex h-9 items-center gap-2 rounded-lg border border-orange-200 bg-white px-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={editRelation.directional}
                onChange={(event) => setEditRelation((current) => ({ ...current, directional: event.target.checked }))}
              />
              Directional
            </label>
          </div>
        </div>
      ) : null}

      {error ? <p className="border-t border-line px-4 py-3 text-sm font-semibold text-red-700">{error}</p> : null}
    </section>
  );
}
